import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import DryRunPanel from "@/components/admin/DryRunPanel";
import CleanAnchorsPanel from "@/components/admin/CleanAnchorsPanel";
import WarningsPanel from "@/components/admin/WarningsPanel";

const ADMIN_EMAIL = "lorenjordan333@gmail.com";

type AdminClient = {
  id: string;
  user_id: string;
  name: string | null;
  email: string | null;
  website_url: string | null;
  tier: string | null;
  status: string | null;
  created_at: string;
  positioning_completed_at: string | null;
  trial_ends_at: string | null;
  plan: string;
};

type AdminStats = {
  total_clients: number;
  with_website: number;
  onboarded: number;
  paying: number;
};

const Admin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [selected, setSelected] = useState<AdminClient | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth", { replace: true });
        return;
      }
      if ((session.user.email ?? "").toLowerCase() !== ADMIN_EMAIL) {
        toast({ title: "Forbidden", description: "Admin access only.", variant: "destructive" });
        navigate("/dashboard", { replace: true });
        return;
      }

      const { data, error } = await supabase.functions.invoke("admin-list-clients");
      if (error) {
        toast({ title: "Failed to load clients", description: error.message, variant: "destructive" });
      } else {
        setClients(data?.clients ?? []);
        setStats(data?.stats ?? null);
      }
      setLoading(false);
    })();
  }, [navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      [c.name, c.email, c.website_url, c.plan].some((v) => v?.toLowerCase().includes(q))
    );
  }, [clients, search]);

  const openClient = async (c: AdminClient) => {
    setSelected(c);
    setDetail(null);
    setDetailLoading(true);
    const { data, error } = await supabase.functions.invoke("admin-get-client-data", {
      body: { client_id: c.id },
    });
    if (error) {
      toast({ title: "Failed to load client data", description: error.message, variant: "destructive" });
    } else {
      setDetail(data);
    }
    setDetailLoading(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="mx-auto max-w-[1200px] px-5 py-10 md:px-10">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Admin · All Clients</h1>
            <p className="text-sm text-muted-foreground mt-1">Restricted view for {ADMIN_EMAIL}</p>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="rounded-[10px] border border-border px-4 py-2 text-sm hover:bg-muted"
          >
            ← My dashboard
          </button>
        </div>

        {stats && (
          <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { label: "Total clients", value: stats.total_clients },
              { label: "With website", value: stats.with_website },
              { label: "Onboarded", value: stats.onboarded },
              { label: "Paying / trial", value: stats.paying },
            ].map((s) => (
              <div key={s.label} className="rounded-[14px] border border-border bg-card p-5">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</div>
                <div className="mt-1 text-3xl font-bold">{s.value}</div>
              </div>
            ))}
          </div>
        )}

        <DryRunPanel />

        <WarningsPanel />

        <CleanAnchorsPanel />


        <LinkAuditPanel />

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, website…"
          className="mb-4 h-11 w-full rounded-[12px] border border-border bg-background px-4 text-sm outline-none"
        />

        <div className="overflow-hidden rounded-[14px] border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Website</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Signed up</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">{c.name || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {c.website_url ? (
                        <a href={c.website_url} target="_blank" rel="noreferrer" className="hover:underline">
                          {c.website_url.replace(/^https?:\/\//, "")}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3"><span className="rounded-full bg-muted px-2 py-1 text-xs">{c.plan}</span></td>
                    <td className="px-4 py-3">{c.status}</td>
                    <td className="px-4 py-3 text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openClient(c)}
                        className="rounded-[8px] bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No clients.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 px-4 py-8" onClick={() => setSelected(null)}>
          <div
            className="max-h-full w-full max-w-[900px] overflow-y-auto rounded-[16px] border border-border bg-card p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <div className="text-xs text-muted-foreground">{selected.email}</div>
                <h2 className="text-xl font-bold">{selected.name}</h2>
                {selected.website_url && (
                  <a href={selected.website_url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">
                    {selected.website_url}
                  </a>
                )}
              </div>
              <button onClick={() => setSelected(null)} className="rounded-[8px] border border-border px-3 py-1.5 text-sm">Close</button>
            </div>

            {detailLoading && <div className="py-10 text-center text-muted-foreground">Loading…</div>}

            {detail && (
              <div className="space-y-6">
                <Section title={`Blog posts (${detail.blog_posts?.length ?? 0})`}>
                  {detail.blog_posts?.slice(0, 10).map((p: any) => (
                    <Row key={p.id} left={p.title || p.slug || "Untitled"} right={p.published_at ? "published" : "draft"} />
                  ))}
                </Section>
                <Section title={`Keywords (${detail.keywords?.length ?? 0})`}>
                  {detail.keywords?.slice(0, 15).map((k: any) => (
                    <Row key={k.id} left={k.keyword} right={k.used ? "used" : "queued"} />
                  ))}
                </Section>
                <Section title={`Rankings (${detail.rankings?.length ?? 0})`}>
                  {detail.rankings?.slice(0, 15).map((r: any) => (
                    <Row key={r.id} left={r.keyword} right={r.rank ? `#${r.rank}` : "—"} />
                  ))}
                </Section>
                <Section title={`Backlinks (${detail.backlinks?.length ?? 0})`}>
                  {detail.backlinks?.slice(0, 10).map((b: any) => (
                    <Row key={b.id} left={b.directory_name} right={b.status} />
                  ))}
                </Section>
                <Section title={`Page speed`}>
                  {detail.page_speed?.[0] ? (
                    <Row left="Latest" right={`Perf ${detail.page_speed[0].performance} · SEO ${detail.page_speed[0].seo}`} />
                  ) : <div className="text-sm text-muted-foreground">No data.</div>}
                </Section>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
    <div className="divide-y divide-border rounded-[10px] border border-border">
      {children}
    </div>
  </div>
);

const Row = ({ left, right }: { left: string; right: string }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
    <span className="truncate">{left}</span>
    <span className="text-muted-foreground">{right}</span>
  </div>
);

type AuditIssue = { post_id: string; title: string | null; slug: string | null; href: string; anchor: string; issue: string };
type AuditClient = { client_id: string; client_name: string | null; expected_host: string; total_posts: number; posts_with_issues: number; issues: AuditIssue[]; issues_count: number };

const LinkAuditPanel = () => {
  const [running, setRunning] = useState(false);
  const [data, setData] = useState<AuditClient[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const run = async () => {
    setRunning(true);
    toast({
      title: "Audit retired",
      description:
        "The internal-link audit was removed in the Writer → Linker → Validator refactor. The validator now rejects bad/duplicate/forced links at publish time.",
    });
    setData([]);
    setRunning(false);
  };

  const totalIssues = (data || []).reduce((s, c) => s + c.issues_count, 0);

  return (
    <div className="mb-8 rounded-[14px] border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold">Internal link audit</h2>
          <p className="text-sm text-muted-foreground">
            Scans every blog post and flags links pointing to the wrong domain.
            {data && ` · ${totalIssues} issue(s) across ${data.filter((d) => d.issues_count > 0).length} client(s).`}
          </p>
        </div>
        <button
          onClick={run}
          disabled={running}
          className="rounded-[10px] bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {running ? "Scanning…" : data ? "Rescan" : "Run audit"}
        </button>
      </div>

      {data && (
        <div className="mt-4 space-y-2">
          {data.filter((c) => c.issues_count > 0).length === 0 && (
            <div className="rounded-[10px] border border-border p-4 text-sm text-muted-foreground">
              No wrong-domain links detected.
            </div>
          )}
          {data.filter((c) => c.issues_count > 0).map((c) => (
            <div key={c.client_id} className="rounded-[10px] border border-border">
              <button
                onClick={() => setOpen(open === c.client_id ? null : c.client_id)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40"
              >
                <div>
                  <div className="font-semibold">{c.client_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">expected host: {c.expected_host || "(none)"} · {c.total_posts} posts</div>
                </div>
                <span className="rounded-full bg-destructive/15 px-2 py-1 text-xs font-semibold text-destructive">
                  {c.issues_count} issue{c.issues_count === 1 ? "" : "s"}
                </span>
              </button>
              {open === c.client_id && (
                <div className="border-t border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-left uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">Post</th>
                        <th className="px-3 py-2">Anchor</th>
                        <th className="px-3 py-2">Wrong link</th>
                        <th className="px-3 py-2">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.issues.slice(0, 200).map((i, idx) => (
                        <tr key={idx} className="border-t border-border">
                          <td className="px-3 py-2">{i.title || i.slug || i.post_id.slice(0, 8)}</td>
                          <td className="px-3 py-2">{i.anchor || "—"}</td>
                          <td className="px-3 py-2 break-all text-destructive">{i.href}</td>
                          <td className="px-3 py-2">{i.issue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Admin;
